/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { CMEQuestionnaireResponseTemplateType } from './cmeQuestionnaireData';
import { RootState } from '../../app/store';
import pearlApi from '../sharedcomponents/api/pearlApi';
import { snackNotifications } from '../sharedcomponents/notification/SnackBarUtils';
import { CMEDataType, CMEState, CMESteps, CMESummaryType, CMETabs } from './cmeTypes';
import { openDocumentByIdThunk, textInputThunk } from '../sharedcomponents/chat/chatSlice';
import listenerMiddleware from '../sharedcomponents/listenerMiddleware';

const currentYear = new Date().getFullYear();
const yearOptionsLength = Math.min(currentYear - 2022, 3);

const initialState: CMEState = {
  currentYearUnclaimed: undefined,
  unclaimed: undefined,
  claimed: undefined,
  certificates: undefined,
  cmeList: [],
  loadingCMESummary: false,
  loadingCount: false,
  selectedDocuments: [],
  fetchingCMEList: false,
  claimingCME: false,
  activeStep: 'selection',
  activeTab: 'unclaimed',
  year: currentYear,
  yearOptions: Array.from({ length: yearOptionsLength }, (_, i: number) => currentYear - i),
};

export const addDocumentCredits = createAsyncThunk<number, string>(
  'addDocumentCredits',
  async (docVersionId: string) => {
    try {
      const {
        data: {
          data: { claimable },
        },
      } = await pearlApi.post(`/cme/credits`, {
        doc_version_id: docVersionId,
      });
      return claimable;
    } catch (err) {
      snackNotifications.error('unable to check/add credits for document read');
      throw err;
    }
  }
);

listenerMiddleware.startListening({
  predicate: (action) =>
    action.type === textInputThunk.fulfilled.type || action.type === openDocumentByIdThunk.fulfilled.type,
  effect: async (action, listenerApi) => {
    const pearlUserInfo = listenerApi.getState().user.pearlUserInfo;
    if (!action.payload || !pearlUserInfo) return;
    const { versions, id } = action.payload;

    if (versions?.length === 1 || id) {
      await listenerApi.dispatch(addDocumentCredits(versions?.[0]?.id || id));
    }
  },
});

export const fetchCMEListThunk = createAsyncThunk<CMEDataType[], void, { state: RootState }>(
  'fetchCMEListThunk',
  async (_, thunkAPI) => {
    try {
      const { cme } = thunkAPI.getState();
      const {
        data: {
          data: { credits },
        },
      } = await pearlApi.get(`/cme/credits?status=${cme.activeTab}&year=${cme.year}`);
      return credits;
    } catch (err) {
      snackNotifications.error(`Failed to fetch cme list: ${(err as Error).message}`);
      throw err;
    }
  }
);

export const getCMECertificateListThunk = createAsyncThunk<CMEDataType[], void, { state: RootState }>(
  'getCMECertificateListThunk',
  async (_, thunkAPI) => {
    try {
      const { cme } = thunkAPI.getState();
      const {
        data: { data },
      } = await pearlApi.get(`/cme/certificates?year=${cme.year}`);
      return data;
    } catch (err) {
      snackNotifications.error(`Failed to fetch cme certificates: ${(err as Error).message}`);
      throw err;
    }
  }
);

// Fetch CME summary
export const fetchCMEPoints = createAsyncThunk<CMESummaryType, void, { state: RootState }>(
  'fetchCMEPoints',
  async (_, thunkAPI) => {
    try {
      const { cme } = thunkAPI.getState();
      const {
        data: { data },
      } = await pearlApi.get(`/cme?year=${cme.year}`);
      return data;
    } catch (err) {
      snackNotifications.error(`Failed to fetch cme points: ${(err as Error).message}`);
    }
  }
);

export const getCMECertificateThunk = createAsyncThunk<void, { url: string; download?: boolean }>(
  'getCMECertificateThunk',
  async ({ url, download }) => {
    try {
      const response = await pearlApi.get(url, {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdf = window.URL.createObjectURL(blob);
      if (!download) {
        const pdfWindow = window.open();
        if (pdfWindow) {
          pdfWindow.location = pdf;
        } else {
          download = true;
        }
      }
      if (download) {
        const link = document.createElement('a');
        link.href = pdf;
        link.download = 'CME-certificate.pdf';
        link.click();
        link.remove();
      }
    } catch (err) {
      snackNotifications.error(`Failed to generate CME certificate`);
      throw err;
    }
  }
);

export const claimCMEThunk = createAsyncThunk<
  { id: string; pdf: string; claimable: number; claimed: number; certificates: number },
  { questionnaire: CMEQuestionnaireResponseTemplateType[] },
  { state: RootState }
>('claimCMEThunk', async ({ questionnaire }, thunkAPI) => {
  try {
    const { cme } = thunkAPI.getState();
    const {
      data: {
        data: { certificate, summary },
      },
    } = await pearlApi.post(`/cme/certificates`, {
      data: {
        credit_ids: cme.selectedDocuments.map(({ id }) => id),
        questionnaire: questionnaire,
      },
    });
    snackNotifications.success('Success, CME claimed. Generating certificate...');
    await thunkAPI.dispatch(getCMECertificateThunk({ url: certificate?.pdf, download: false }));
    await thunkAPI.dispatch(fetchCMEListThunk());
    return summary;
  } catch (err) {
    snackNotifications.error(`Failed to claim CME: ${(err as Error).message}`);
    throw err;
  }
});

export const cmeSlice = createSlice({
  name: 'cme',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setActiveStep: (state, action: PayloadAction<CMESteps>) => {
      state.activeStep = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<CMETabs>) => {
      state.activeTab = action.payload;
    },
    setYear: (state, action: PayloadAction<number>) => {
      state.year = action.payload;
    },
    selectCMEDocuments: (state, action: PayloadAction<CMEDataType[]>) => {
      state.selectedDocuments = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCMEListThunk.pending, (state) => {
        state.cmeList = [];
        state.fetchingCMEList = true;
      })
      .addCase(fetchCMEListThunk.fulfilled, (state, data) => {
        state.cmeList = data.payload;
        state.fetchingCMEList = false;
      })
      .addCase(fetchCMEListThunk.rejected, (state) => {
        state.fetchingCMEList = false;
      })
      .addCase(getCMECertificateListThunk.pending, (state) => {
        state.cmeList = [];
        state.fetchingCMEList = true;
      })
      .addCase(getCMECertificateListThunk.fulfilled, (state, data) => {
        state.cmeList = data.payload;
        state.fetchingCMEList = false;
      })
      .addCase(getCMECertificateListThunk.rejected, (state) => {
        state.fetchingCMEList = false;
      })
      .addCase(claimCMEThunk.pending, (state) => {
        state.claimingCME = true;
        state.loadingCount = true;
        if (currentYear === state.year) {
          state.loadingCMESummary = true;
        }
      })
      .addCase(claimCMEThunk.fulfilled, (state, data) => {
        state.unclaimed = data.payload?.claimable;
        state.claimed = data.payload?.claimed;
        state.certificates = data.payload?.certificates;
        if (currentYear === state.year) {
          state.currentYearUnclaimed = data.payload?.claimable;
        }
        state.activeStep = 'selection';
        state.claimingCME = false;
        state.loadingCMESummary = false;
        state.loadingCount = false;
      })
      .addCase(claimCMEThunk.rejected, (state) => {
        state.claimingCME = false;
        state.loadingCMESummary = false;
        state.loadingCount = false;
      })
      .addCase(addDocumentCredits.fulfilled, (state, data) => {
        state.currentYearUnclaimed = data.payload;
      })
      .addCase(fetchCMEPoints.pending, (state) => {
        state.unclaimed = undefined;
        state.claimed = undefined;
        state.certificates = undefined;
        state.loadingCount = true;
        if (currentYear === state.year && state.currentYearUnclaimed === undefined) {
          state.loadingCMESummary = true;
        }
      })
      .addCase(fetchCMEPoints.fulfilled, (state, data) => {
        state.unclaimed = data.payload?.claimable;
        state.claimed = data.payload?.claimed;
        state.certificates = data.payload?.certificates;
        state.loadingCount = false;
        if (currentYear === state.year) {
          state.currentYearUnclaimed = data.payload?.claimable;
        }
        state.loadingCMESummary = false;
      })
      .addCase(fetchCMEPoints.rejected, (state) => {
        state.loadingCMESummary = false;
        state.loadingCount = false;
      });
  },
});

export const { selectCMEDocuments, setActiveStep, setActiveTab, setYear } = cmeSlice.actions;

export const selectCMEState = (state: RootState): CMEState => state.cme;
export const selectActiveTab = (state: RootState): CMETabs => state.cme.activeTab;
export const selectClaimingCME = (state: RootState): boolean => state.cme.claimingCME;
export const selectCMESelectedDocuments = (state: RootState): CMEDataType[] => state.cme.selectedDocuments;

export default cmeSlice.reducer;
